<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { hideTonPayment, useInit } from '@/composables/init'
import TheHeader from '@/components/TheHeader.vue'
import { computed, type ComputedRef, onBeforeMount, onMounted, provide, ref, watch } from 'vue'
import TheNavbar from '@/components/TheNavbar.vue'
import { usePlayer, wasDormant } from '@/composables/game/player'
import FlyingCoins from '@/components/FlyingCoins.vue'
import { useAnimateCoins, useFlyingCoins } from '@/composables/game/flying-coins'
import OfflineProfitModal from '@/components/modals/OfflineProfitModal.vue'
import Toaster from '@/components/TheToaster.vue'
import AllDailyDoneOverlay from '@/components/overlays/AllDailyDoneOverlay.vue'
import { useAllDailyCheck, useDailyRadarTx, useDailyTx } from '@/composables/game/daily'
import LevelUpModal from '@/components/modals/LevelUpModal.vue'
import { useConfig } from '@/composables/game/config'
import TokenChangedModal from '@/components/modals/TokenChangedModal.vue'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import router from '@/router'
import { RouteName, routePath } from '@/routes'
import ExtraSpinOverlay from '@/components/overlays/ExtraSpinOverlay.vue'
import { useLocalStorage, watchOnce } from '@vueuse/core'
import { useTranslate } from '@tolgee/vue'
import { useTaskHighlight } from '@/composables/game/task-highlight'
import TaskHighlightModal from '@/components/modals/TaskHighlightModal.vue'
import { useOnboarding } from '@/composables/game/onboarding'
import { useGuide } from '@/composables/game/guide'
import StarBalanceConfirmModal from '@/components/modals/StarBalanceConfirmModal.vue'
import {
  type Badge,
  type DailyUpWinnerNotification,
  isBadgeNotification,
  isDailyUpWinnerNotification,
  isMemoryGameChallengeWinnerNotification,
  isMinesweeperGameChallengeWinnerNotification,
  isPathwayChallengeWinnerNotification,
  isPuzzleChallengeWinnerNotification,
  isSpecialCollectibleChallengeWinnerNotification,
  type MemoryGameChallengeWinnerNotification,
  type MinesweeperGameChallengeWinnerNotification,
  type PathwayChallengeWinnerNotification,
  type PuzzleChallengeWinnerNotification,
  type SpecialCollectibleWinnerNotification,
} from '@/types/shared/player'
import { useConfetti } from '@/composables/game/confetti'
import { availableBadgesMap, useBadges } from '@/composables/game/badge'
import ChallengeModal from '@/components/modals/CardChallengeModal.vue'
import { useCardChallengeLeaderboard } from '@/composables/game/leader-board'
import NumberFormattingModal from '@/components/modals/NumberFormattingModal.vue'
import { useModals } from '@/composables/modal-helper'
import DailyTonTransactionModal from '@/components/modals/DailyTonTransactionModal.vue'
import BonusOverlay from '@/components/overlays/BonusOverlay.vue'
import AchievementOverlay from '@/components/overlays/AchievementOverlay.vue'
import { useAchievementsPage, useAchievementsTracker } from '@/composables/game/achievements'
import DailyRadarModal from '@/components/modals/DailyRadarModal.vue'
import useIsScreenSmallerThan from '@/composables/helpers/is-screen-smaller-than'
import WebApp from '@twa-dev/sdk'
import PathwayEventOverlay from '@/components/overlays/PathwayEventOverlay.vue'
import { usePathwayEventOverlay } from '@/composables/game/pathway-events'
import PuzzlesOverlay from '@/components/overlays/PuzzlesOverlay.vue'
import { usePuzzlesOverlay } from '@/composables/game/puzzles'
import DailyUpChallengeModal from '@/components/modals/DailyUpChallengeModal.vue'
import BadgeOverlay from '@/components/overlays/BadgeOverlay.vue'
import DailyUpChallengeWinnerModal from '@/components/modals/DailyUpChallengeWinnerModal.vue'
import PathwayChallengeModal from '@/components/modals/PathwayChallengeModal.vue'
import PathwayChallengeWinnerModal from '@/components/modals/PathwayChallengeWinnerModal.vue'
import MemoryChallengeOverlay from '@/components/overlays/MemoryChallengeOverlay.vue'
import MemoryChallengeWinnerModal from '@/components/modals/MemoryChallengeWinnerModal.vue'
import { useMemoryChallengeModal } from '@/composables/game/memory-challenge-modal'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import PuzzleChallengeModal from '@/components/modals/PuzzleChallengeModal.vue'
import PuzzleChallengeWinnerModal from '@/components/modals/PuzzleChallengeWinnerModal.vue'
import { useRouterRemoveQueryParam } from '@/composables/game/router'
import { MINI_GAME_ID } from '@/types/shared/player-config'
import MinesweeperChallengeOverlay from '@/components/overlays/MinesweeperChallengeOverlay.vue'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import { useGiftModal } from '@/composables/game/gift-modal'
import ViewGiftModal from '@/components/modals/ViewGiftModal.vue'
import MinesweeperChallengeWinnerModal from '@/components/modals/MinesweeperChallengeWinnerModal.vue'
import CollectibleChallengeOverlay from '@/components/overlays/CollectibleChallengeOverlay.vue'
import { useCollectibleChallengeModal } from '@/composables/game/collectible-challenge-modal'
import CollectibleChallengeWinnerModal from '@/components/modals/CollectibleChallengeWinnerModal.vue'
import DropeePresaleModal from '@/components/modals/DropeePresaleModal.vue'

type ExtraSpinDetails = {
	count: number,
	description: string
}

const route = useRoute()
const meta = computed(() => route.meta)
const hideHeader = computed((): boolean => Boolean(meta.value.hideHeader) ?? false)
const hideNavbar = computed((): boolean => Boolean(meta.value.hideNavbar) ?? false)
const { coins, offlineProfit, player, chosenToken, offlineProfitHours, notifications, notificationReward } = usePlayer()
const { config } = useConfig()
const { showFlyingCoins, startCoinAnimation } = useFlyingCoins()
const { allDailyDone } = useAllDailyCheck()
const { doAnimateCoins } = useAnimateCoins()
const { t } = useTranslate()
const { isOnboardingDone } = useOnboarding()
const { initGuide } = useGuide()
const { showConfetti } = useConfetti()
const { getBadges } = useBadges()
const { setCardChallengeModalTimestamp } = useCardChallengeLeaderboard()
const { claimedDailyTx, hasDoneTxToday, setDailyTxModalTimestamp, canShowDailyTxModal: canShowTx } = useDailyTx()
const {
  claimedDailyRadar,
  hasDoneRadarTxToday,
  setDailyRadarTxModalTimestamp,
  canShowDailyRadarTxModal: canShowRadarTx,
} = useDailyRadarTx()
const {
  showNumberFormatModal,
  setShowNumberFormatModal,
  showTransactionModal,
  setShowTransactionModal,
  showRadarTransactionModal,
  setShowRadarTransactionModal,
  showDailyUpChallengeModal,
  setShowDailyUpChallengeModal,
  showPathwayChallengeModal,
  setShowPathwayChallengeModal,
  showPuzzleChallengeModal,
  setShowPuzzleChallengeModal,
  showPresaleModal,
  setShowPresaleModal,
} = useModals()
const { selectedGift, showGiftModal, hideGiftModal } = useGiftModal()
useAchievementsPage()
const {
  showNewAchievedMilestoneOverlay,
  newAchievedMilestoneForOverlay,
  checkForNewAchievementsOnInit,
} = useAchievementsTracker()
const {
  showPathwayEventOverlay,
  selectedPathwayEvent,
  hidePathwayEventOverlay,
} = usePathwayEventOverlay()
const {
  showPuzzlesOverlay,
  hidePuzzlesOverlay,
} = usePuzzlesOverlay()
const {
  openMemoryChallengeModal,
  showMemoryChallengeModal,
  closeMemoryChallengeModal,
} = useMemoryChallengeModal()
const {
  openMinesweeperChallengeModal,
  showMinesweeperChallengeModal,
  closeMinesweeperChallengeModal,
} = useMinesweeperChallengeModal()
const {
  showCollectibleChallengeModal,
  closeCollectibleChallengeModal,
} = useCollectibleChallengeModal()
const { updateState } = useFortuneWheel()
const { newHighlightedTasks, setNewTasksAsClosed } = useTaskHighlight()
const { bottomNumber } = useFullScreenDimensions()
const { removeQueryParams } = useRouterRemoveQueryParam()

const canShowDailyChallengeModal = computed(() => false) //hide the modal from the flow for now
const canShowBonusOverlay = ref(true)
const canShowOfflineProfitModal = ref(false)
const canShowDailyTxModal = computed(() => canShowTx.value && isOnboardingDone.value && !claimedDailyTx.value && !hasDoneTxToday.value && isInitLoad.value && !hideTonPayment.value)
const canShowRadarTxModal = computed(() => canShowRadarTx.value && isOnboardingDone.value && !claimedDailyRadar.value && !hasDoneRadarTxToday.value && isInitLoad.value && !hideTonPayment.value)

const dailyUpWinnerNotification = ref<null | DailyUpWinnerNotification>(null)
const pathwayChallengeWinnerNotification = ref<null | PathwayChallengeWinnerNotification>(null)
const puzzleChallengeWinnerNotification = ref<null | PuzzleChallengeWinnerNotification>(null)
const memoryGameWinnerNotification = ref<null | MemoryGameChallengeWinnerNotification>(null)
const minesweeperGameWinnerNotification = ref<null | MinesweeperGameChallengeWinnerNotification>(null)
const specialCollectibleWinnerNotification = ref<null | SpecialCollectibleWinnerNotification>(null)

const isSmallScreen = useIsScreenSmallerThan(375)
const isInitLoad = ref(true)
const coinsWithoutOfflineProfit = computed(() => coins.value - offlineProfit.value)
const addOfflineProfitToCoins = ref(false)
const coinsToShow = computed(() => addOfflineProfitToCoins.value ? coins.value : coinsWithoutOfflineProfit.value)
provide<ComputedRef<number>>('coinsToShow', coinsToShow)

const hasNewHighlightedTasks = computed(() => newHighlightedTasks.value.length > 0)
const hasBonusWithTphX2 = ref(false)

const showBonusOverlay = ref(false)
const showOfflineProfitModal = ref(false)
const showLevelUpModal = ref(false)
const showAllDailyDoneOverlay = ref(false)
const showTokenChangedModal = ref(false)
const showUpgradesExtraSpinOverlay = ref(false)
const showHighlightedTasksModal = ref(false)
const showBadgeOverlay = ref(false)
const showChallengeModal = ref(false)

const badge = ref<Badge | undefined>()
const currentNotificationIndex = ref<number | undefined>()
const badgeNotifications = computed(() => notifications.value.filter(isBadgeNotification))
const dailyUpWinnerNotifications = computed(() => notifications.value.filter(isDailyUpWinnerNotification))
const pathwayChallengeWinnerNotifications = computed(() => notifications.value.filter(isPathwayChallengeWinnerNotification))
const memoryGameWinnerNotifications = computed(() => notifications.value.filter(isMemoryGameChallengeWinnerNotification))
const minesweeperGameWinnerNotifications = computed(() => notifications.value.filter(isMinesweeperGameChallengeWinnerNotification))
const puzzleChallengeWinnerNotifications = computed(() => notifications.value.filter(isPuzzleChallengeWinnerNotification))
const specialCollectibleWinnerNotifications = computed(() => notifications.value.filter(isSpecialCollectibleChallengeWinnerNotification))

const playerLevel = computed(() => player.value.level)
const playerTapConfig = computed(() => config.value.tap)

const upgradesExtraSpinDetails = computed<ExtraSpinDetails>(() => {
  return {
    count: config.value.game.fortuneWheel.extraSpin.upgrades.amount,
    description: t.value('fortune_wheel.extra_spin.upgrades.description', 'You successfully upgraded 10 cards. Head to Rewards and spin the wheel to earn more!'),
  }
})
const upgradesExtraSpinOverlayDisplayed = useLocalStorage<boolean>('upgradesExtraSpinOverlayDisplayed', false)

const fullScreenStyles = computed(() => {
  return { paddingBottom: `${bottomNumber.value + (hideNavbar.value ? -20 : isSmallScreen.value ? 60 : 70)}px` }
})

function checkForChallengeModal() {
  if (canShowDailyChallengeModal.value) {
    setTimeout(() => {
      setCardChallengeModalTimestamp()
      showChallengeModal.value = true
    }, 600)
  } else {
    checkForDailyUpWinner()
  }
}

function checkForDailyUpWinner() {
  if (dailyUpWinnerNotifications.value.length) {
    dailyUpWinnerNotification.value = dailyUpWinnerNotifications.value[0]
  } else {
    checkForPathwayChallengeWinner()
  }
}

function checkForPathwayChallengeWinner() {
  if (pathwayChallengeWinnerNotifications.value.length) {
    pathwayChallengeWinnerNotification.value = pathwayChallengeWinnerNotifications.value[0]
  } else {
    checkForPuzzleChallengeWinner()
  }
}

function checkForPuzzleChallengeWinner() {
  if (puzzleChallengeWinnerNotifications.value.length) {
    puzzleChallengeWinnerNotification.value = puzzleChallengeWinnerNotifications.value[0]
  } else {
    checkForMemoryGameWinner()
  }
}

function checkForMemoryGameWinner() {
  if (memoryGameWinnerNotifications.value.length) {
    memoryGameWinnerNotification.value = memoryGameWinnerNotifications.value[0]
  } else {
    checkForMinesweeperGameWinner()
  }
}

function checkForMinesweeperGameWinner() {
  if (minesweeperGameWinnerNotifications.value.length) {
    minesweeperGameWinnerNotification.value = minesweeperGameWinnerNotifications.value[0]
  } else {
    checkForSpecialCollectibleWinner()
  }
}

function checkForSpecialCollectibleWinner() {
  if (specialCollectibleWinnerNotifications.value.length) {
    specialCollectibleWinnerNotification.value = specialCollectibleWinnerNotifications.value[0]
  } else {
    checkForNewBadge()
  }
}

function onHideDailyUpWinnerModal() {
  dailyUpWinnerNotification.value = null
  checkForPathwayChallengeWinner()
}

function onHidePathwayChallengeWinnerModal() {
  pathwayChallengeWinnerNotification.value = null
  checkForPuzzleChallengeWinner()
}

function onHidePuzzleChallengeWinnerModal() {
  puzzleChallengeWinnerNotification.value = null
  checkForMemoryGameWinner()
}

function onHideMemoryGameWinnerModal() {
  memoryGameWinnerNotification.value = null
  checkForMinesweeperGameWinner()
}

function onHideMinesweeperGameWinnerModal() {
  minesweeperGameWinnerNotification.value = null
  checkForSpecialCollectibleWinner()
}

function onHideSpecialCollectibleWinnerModal() {
  specialCollectibleWinnerNotification.value = null
  checkForNewBadge()
}

function onHideChallengeModal() {
  showChallengeModal.value = false
  checkForDailyUpWinner()
}

function setBadgeFromNotification(badgeIndex: number) {
  const badgeNotification = badgeNotifications.value[badgeIndex]
  const badgeId = badgeNotification.badgeId
  badge.value = availableBadgesMap[badgeId]
  const originalIndex = notifications.value.findIndex(n => isBadgeNotification(n) && n.badgeId === badgeId)
  if (originalIndex !== -1) {
    notifications.value.splice(originalIndex, 1)
  }
}

function checkForHighlightedTasks() {
  if (showTransactionModal.value) return
  showHighlightedTasksModal.value = hasNewHighlightedTasks.value
  if (!showHighlightedTasksModal.value) {
    checkForNewAchievementsOnInit()
  }
}

function checkForNewBadge() {
  if (badgeNotifications.value.length) {
    getBadges()
    setBadgeFromNotification(0)
    setTimeout(() => {
      showBadgeOverlay.value = true
      currentNotificationIndex.value = 0
    }, 600)
  } else {
    checkForHighlightedTasks()
  }
}

function onBadgeOverlayClosed() {
  showBadgeOverlay.value = false
  checkForHighlightedTasks()
}

function closeOfflineProfitModal() {
  startCoinAnimation()
  setTimeout(() => {
    showOfflineProfitModal.value = false
    doAnimateCoins()
    setTimeout(() => {
      addOfflineProfitToCoins.value = true
    }, 250)
    setTimeout(() => {
      checkForDailyTxModals()
    }, 400)
  }, 150)
}

function checkForDailyTxModals() {
  if (canShowRadarTxModal.value) {
    setShowRadarTransactionModal(true)
    return
  }
  checkForDailyTonModal()
}

function checkForDailyTonModal() {
  if (canShowDailyTxModal.value) {
    setShowTransactionModal(true)
    return
  }
  checkForChallengeModal()
}

function closeDailyRadarTransactionModal() {
  setDailyRadarTxModalTimestamp()
  setShowRadarTransactionModal(false)
  setTimeout(() => {
    checkForDailyTonModal()
  }, 500)
}

function closeDailyTonTransactionModal() {
  setDailyTxModalTimestamp()
  setShowTransactionModal(false)
  isInitLoad.value = false
  setTimeout(() => {
    checkForChallengeModal()
  }, 500)
}

function hideUpgradesExtraSpinOverlay() {
  showUpgradesExtraSpinOverlay.value = false
  upgradesExtraSpinOverlayDisplayed.value = true
}

function closeHighlightModal() {
  showHighlightedTasksModal.value = false
  setNewTasksAsClosed()
  checkForNewAchievementsOnInit()
}

function closeBonusOverlay() {
  showBonusOverlay.value = false
  if (hasBonusWithTphX2.value && canShowBonusOverlay.value && (notificationReward.value.coins || notificationReward.value.spins)) {
    hasBonusWithTphX2.value = false
    setTimeout(() => {
      showBonusOverlay.value = true
    }, 500)
  } else {
    canShowBonusOverlay.value = false
    if (canShowOfflineProfitModal.value) {
      showOfflineProfitModal.value = true
    } else {
      checkForDailyTxModals()
    }
  }
}

function goToHighlightedTask(taskId: string) {
  closeHighlightModal()
  router.push({ name: RouteName.EARN, query: { openTaskId: taskId } })
}

watchOnce(offlineProfit, (offlineProfit, oldValue) => {
  if (oldValue !== undefined || offlineProfit < 1) {
    if (!player.value.onboarding?.done) return
    checkForDailyTxModals()
    return
  }
  canShowOfflineProfitModal.value = true
}, { immediate: true })

watch(allDailyDone, (allDailyDone) => {
  if (!allDailyDone) return
  if (router.currentRoute.value.fullPath !== routePath[RouteName.REWARDS]) {
    showAllDailyDoneOverlay.value = true
  }
})

useInit()

watch(playerLevel, () => {
  showLevelUpModal.value = true
  updateState()
})

watch(chosenToken, () => {
  setTimeout(() => {
    showTokenChangedModal.value = true
  }, 300)
})

watch(() => player.value.upgradeCount, () => {
  if (player.value.upgradeCount && !(player.value.upgradeCount % 10) && !upgradesExtraSpinOverlayDisplayed.value) {
    setTimeout(() => {
      showUpgradesExtraSpinOverlay.value = true
    }, 1000)
  } else {
    upgradesExtraSpinOverlayDisplayed.value = false
  }
})

watch(() => [showBadgeOverlay.value, currentNotificationIndex.value], () => {
  if (!showBadgeOverlay.value && currentNotificationIndex.value !== undefined && notifications.value[currentNotificationIndex.value + 1]) {
    setTimeout(() => {
      if (currentNotificationIndex.value !== undefined) {
        setBadgeFromNotification(currentNotificationIndex.value + 1)
        showBadgeOverlay.value = true
        currentNotificationIndex.value += 1
      }
    }, 600)
  } else if (showBadgeOverlay.value) {
    setTimeout(() => {
      showConfetti()
    }, 600)
  }
  if (!showBadgeOverlay.value && currentNotificationIndex.value !== undefined && !notifications.value[currentNotificationIndex.value + 1]) {
    checkForHighlightedTasks()
  }
})

watch(canShowOfflineProfitModal, () => {
  if (!showBonusOverlay.value && canShowBonusOverlay.value && notificationReward.value.tphX2) {
    hasBonusWithTphX2.value = true
    showBonusOverlay.value = true
  } else if (!showBonusOverlay.value && canShowBonusOverlay.value && (notificationReward.value.coins || notificationReward.value.spins)) {
    showBonusOverlay.value = true
  } else if (canShowOfflineProfitModal.value && !showBonusOverlay.value) {
    showOfflineProfitModal.value = true
  } else if (!showBonusOverlay.value) {
    checkForDailyTxModals()
  }
}, { immediate: true })

onBeforeMount(async () => {
  WebApp.setBackgroundColor('#000000')
  WebApp.setBottomBarColor('#000000')
  WebApp.disableVerticalSwipes()

  if (!isOnboardingDone.value) {
    initGuide()
    await router.push({ name: RouteName.REWARDS })
  } else if (wasDormant.value) {
    await router.push({ name: RouteName.REWARDS })
  }
})

function checkForMiniGameReturn() {
  if (route.query.gameId) {
    if (route.query.gameId === MINI_GAME_ID.MEMORY_GAME) {
      openMemoryChallengeModal(true)
    } else if (route.query.gameId === MINI_GAME_ID.MINESWEEPER) {
      openMinesweeperChallengeModal(true)
    }
    removeQueryParams(['gameSubmitted', 'gameId'])
  }
}

onMounted(() => {
  checkForMiniGameReturn()
})
</script>

<template>
  <FlyingCoins :show="showFlyingCoins" />
  <div
    id="confetti-container"
    class="fixed top-0 z-[9999] left-0 w-screen h-screen pointer-events-none"
    style="will-change: transform;"
  />

  <div
    class="flex-1 flex flex-col"
    :class="router.currentRoute.value.name === RouteName.HOME && 'h-full'"
    :style="fullScreenStyles"
  >
    <TheHeader v-if="!hideHeader" />

    <RouterView />

    <TheNavbar v-if="!hideNavbar" />

    <BonusOverlay
      v-if="showBonusOverlay"
      :show="showBonusOverlay"
      :double-profit="hasBonusWithTphX2 ? notificationReward.tphX2 : 0"
      :coins="hasBonusWithTphX2 ? 0 : notificationReward.coins"
      :spins="hasBonusWithTphX2 || notificationReward.tickets ? 0 : notificationReward.spins"
      :tickets="hasBonusWithTphX2 ? 0 : notificationReward.tickets"
      @cta-click="closeBonusOverlay"
      @on-check-entries="router.push({name:RouteName.GIVEAWAY})"
    />

    <OfflineProfitModal
      v-if="showOfflineProfitModal"
      :show="showOfflineProfitModal"
      :profit="offlineProfit"
      :offline-profit-hours="offlineProfitHours"
      @hide="closeOfflineProfitModal"
    />

    <DailyTonTransactionModal
      v-if="showTransactionModal"
      :show="showTransactionModal"
      :is-done="claimedDailyTx"
      @hide="closeDailyTonTransactionModal"
    />

    <DailyRadarModal
      v-if="showRadarTransactionModal"
      :show="showRadarTransactionModal"
      :is-done="claimedDailyRadar"
      @hide="closeDailyRadarTransactionModal"
    />

    <LevelUpModal
      v-if="showLevelUpModal"
      :show="showLevelUpModal"
      :tap-config="playerTapConfig"
      :current-level="playerLevel"
      @hide="showLevelUpModal = false"
    />

    <AllDailyDoneOverlay
      v-if="showAllDailyDoneOverlay"
      :class="showAllDailyDoneOverlay ? 'z-[999] opacity-100' : '-z-[1] opacity-0'"
      :show="showAllDailyDoneOverlay"
      @click="showAllDailyDoneOverlay = false"
    />

    <ExtraSpinOverlay
      v-if="showUpgradesExtraSpinOverlay"
      :show="showUpgradesExtraSpinOverlay"
      :spin-count="upgradesExtraSpinDetails.count"
      :description="upgradesExtraSpinDetails.description"
      @click="hideUpgradesExtraSpinOverlay"
    />

    <TokenChangedModal
      v-if="showTokenChangedModal"
      :show="showTokenChangedModal"
      :token="chosenToken"
      @hide="showTokenChangedModal = false"
    />

    <TaskHighlightModal
      v-if="showHighlightedTasksModal"
      :show="showHighlightedTasksModal"
      :tasks="newHighlightedTasks"
      @open-task="goToHighlightedTask"
      @hide="closeHighlightModal"
    />

    <StarBalanceConfirmModal />

    <BadgeOverlay
      v-if="showBadgeOverlay"
      :show="showBadgeOverlay"
      :title="t('badge_overlay.title', 'Congrats, you won a badge!')"
      :name="badge?.name"
      :image="badge?.imageUrl"
      @click="onBadgeOverlayClosed"
    />

    <ChallengeModal
      v-if="showChallengeModal"
      :show="showChallengeModal"
      @hide="onHideChallengeModal"
    />

    <DailyUpChallengeModal
      v-if="showDailyUpChallengeModal"
      :show="showDailyUpChallengeModal"
      @hide="setShowDailyUpChallengeModal(false)"
    />

    <DailyUpChallengeWinnerModal
      v-if="dailyUpWinnerNotification"
      :show="!!dailyUpWinnerNotification"
      :notification="dailyUpWinnerNotification"
      @hide="onHideDailyUpWinnerModal"
    />

    <PathwayChallengeWinnerModal
      v-if="pathwayChallengeWinnerNotification"
      :show="!!pathwayChallengeWinnerNotification"
      :notification="pathwayChallengeWinnerNotification"
      @hide="onHidePathwayChallengeWinnerModal"
    />

    <PuzzleChallengeWinnerModal
      v-if="puzzleChallengeWinnerNotification"
      :show="!!puzzleChallengeWinnerNotification"
      :notification="puzzleChallengeWinnerNotification"
      @hide="onHidePuzzleChallengeWinnerModal"
    />

    <MemoryChallengeWinnerModal
      v-if="memoryGameWinnerNotification"
      :show="!!memoryGameWinnerNotification"
      :notification="memoryGameWinnerNotification"
      @hide="onHideMemoryGameWinnerModal"
    />

    <MinesweeperChallengeWinnerModal
      v-if="minesweeperGameWinnerNotification"
      :show="!!minesweeperGameWinnerNotification"
      :notification="minesweeperGameWinnerNotification"
      @hide="onHideMinesweeperGameWinnerModal"
    />

    <CollectibleChallengeWinnerModal
      v-if="specialCollectibleWinnerNotification"
      :show="!!specialCollectibleWinnerNotification"
      :notification="specialCollectibleWinnerNotification"
      @hide="onHideSpecialCollectibleWinnerModal"
    />

    <NumberFormattingModal
      v-if="showNumberFormatModal"
      :show="showNumberFormatModal"
      @hide="setShowNumberFormatModal(false)"
    />

    <AchievementOverlay
      v-if="showNewAchievedMilestoneOverlay"
      :show="showNewAchievedMilestoneOverlay"
      :achievement="newAchievedMilestoneForOverlay?.achievement"
      :category-title="newAchievedMilestoneForOverlay?.categoryTitle"
      @hide="newAchievedMilestoneForOverlay = null"
    />

    <PathwayEventOverlay
      v-if="showPathwayEventOverlay && selectedPathwayEvent"
      :show="showPathwayEventOverlay"
      :pathway-event="selectedPathwayEvent"
      @hide="hidePathwayEventOverlay"
    />

    <PuzzlesOverlay
      v-if="showPuzzlesOverlay"
      :show="showPuzzlesOverlay"
      @hide="hidePuzzlesOverlay"
    />

    <PathwayChallengeModal
      v-if="showPathwayChallengeModal"
      :show="showPathwayChallengeModal"
      @hide="setShowPathwayChallengeModal(false)"
    />

    <PuzzleChallengeModal
      v-if="showPuzzleChallengeModal"
      :show="showPuzzleChallengeModal"
      @hide="setShowPuzzleChallengeModal(false)"
    />

    <MemoryChallengeOverlay
      v-if="showMemoryChallengeModal"
      :show="showMemoryChallengeModal"
      @hide="closeMemoryChallengeModal"
      @play="closeMemoryChallengeModal"
    />

    <MinesweeperChallengeOverlay
      v-if="showMinesweeperChallengeModal"
      :show="showMinesweeperChallengeModal"
      @hide="closeMinesweeperChallengeModal"
      @play="closeMinesweeperChallengeModal"
    />

    <CollectibleChallengeOverlay
      v-if="showCollectibleChallengeModal"
      :show="showCollectibleChallengeModal"
      @hide="closeCollectibleChallengeModal"
      @play="closeCollectibleChallengeModal"
    />

    <ViewGiftModal
      v-if="showGiftModal && selectedGift"
      :show="showGiftModal"
      :gift="selectedGift"
      modal-wrapper-class="z-[1000]"
      @hide="hideGiftModal"
    />

    <DropeePresaleModal
      v-if="showPresaleModal"
      :show="showPresaleModal"
      @hide="setShowPresaleModal(false)"
    />
  </div>
  <Toaster class="fixed top-0 z-[999]" />
</template>

<style scoped>
.fade-enter-active {
	transition: opacity 0.6s ease-in;
	opacity: 1;
}

.fade-leave-active {
	transition: opacity 0.6s ease-out;
	opacity: 1;
}

.fade-enter-from {
	opacity: 0;
}

.fade-leave-to {
	opacity: 0;
}

.bg-fill-gradient {
	background: linear-gradient(180deg, #000 30%, rgba(0, 0, 0, 0.00) 100%);
}

</style>
